import { FLUX_LORA_GENERATION_COST } from "./fal-ai-costs";

type CostConfig = {
  type: "fixed" | "per_second";
  value: number; // For fixed this is the flat cost, for per_second this is the cost per second
};

type OutputMapping = {
  [outputKey: string]: string;
};

type DynamicInput = {
  type: "training_image" | string;
  field: string;
};

export type WorkflowStep = {
  type: "fal-workflow" | "fal-comfyui-workflow";
  service: string;
  webhook_url: string;
  outputPath: string;
  outputMapping?: OutputMapping;
  costConfig: CostConfig;
  dynamicInputs?: DynamicInput[];
};

type WorkflowPipeline = {
  [step: number]: WorkflowStep;
};

export const INITIAL_GENERATION_SERVICE =
  "workflows/Prompt-Hunt/initial-generation-production";
export const INITIAL_GENERATION_USER_LORA_EXCLUDED_SERVICE =
  "workflows/Prompt-Hunt/initial-generation-user-lora-excluded-production";
export const TRY_ON_SERVICE = "workflows/Prompt-Hunt/try-on-production";
export const FACEDETAILER_SERVICE = "comfy/Prompt-Hunt/facedetailer-production";
export const REDO_FACE_SERVICE = "comfy/Prompt-Hunt/redo-face-production";
export const FAL_AI_CCSR_SERVICE = "fal-ai/ccsr";
export const FAL_AI_RECAFT_CLARITY_UPSCALE_SERVICE =
  "fal-ai/recraft-clarity-upscale";

export const FAL_AI_FILL_IMAGE_SERVICE = "fal-ai/bria/expand";

// Cost constants
export const COMFY_PER_SECOND_COST = 0.00111; // Cost per second for comfy workflows

export const TRY_ON_WORKFLOW_PIPELINE: WorkflowPipeline = {
  1: {
    type: "fal-workflow",
    service: INITIAL_GENERATION_SERVICE,
    webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
    outputPath: "payload.images[0]",
    outputMapping: {
      url: "image_url",
    },
    costConfig: {
      type: "fixed",
      value: FLUX_LORA_GENERATION_COST,
    },
  },
  2: {
    type: "fal-workflow",
    service: TRY_ON_SERVICE,
    webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
    outputPath: "payload.images[0]",
    outputMapping: {
      url: "image_url",
    },
    costConfig: {
      type: "per_second",
      value: COMFY_PER_SECOND_COST,
    },
  },
  3: {
    type: "fal-comfyui-workflow",
    service: FACEDETAILER_SERVICE,
    webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
    outputPath: "payload.outputs",
    costConfig: {
      type: "per_second",
      value: COMFY_PER_SECOND_COST,
    },
  },
};

export const HEADSHOT_WORKFLOW_PIPELINE: WorkflowPipeline = {
  1: {
    type: "fal-workflow",
    service: INITIAL_GENERATION_SERVICE,
    webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
    outputPath: "payload.images[0]",
    outputMapping: {
      url: "image_url",
    },
    costConfig: {
      type: "fixed",
      value: FLUX_LORA_GENERATION_COST,
    },
  },
  2: {
    type: "fal-comfyui-workflow",
    service: FACEDETAILER_SERVICE,
    webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
    outputPath: "payload.outputs",
    costConfig: {
      type: "per_second",
      value: COMFY_PER_SECOND_COST,
    },
  },
};

export const HEADSHOT_WORKFLOW_PIPELINE_PASS1_USER_LORA_EXCLUDED: WorkflowPipeline =
  {
    1: {
      type: "fal-workflow",
      service: INITIAL_GENERATION_USER_LORA_EXCLUDED_SERVICE,
      webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
      outputPath: "payload.images[0]",
      outputMapping: {
        url: "image_url",
      },
      costConfig: {
        type: "fixed",
        value: FLUX_LORA_GENERATION_COST,
      },
    },
    2: {
      type: "fal-comfyui-workflow",
      service: REDO_FACE_SERVICE,
      webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
      outputPath: "payload.outputs",
      costConfig: {
        type: "per_second",
        value: COMFY_PER_SECOND_COST,
      },
      dynamicInputs: [
        {
          type: "training_image",
          field: "training_image_url",
        },
      ],
    },
  };

export const SINGLE_PASS_HEADSHOT_WORKFLOW: WorkflowPipeline = {
  1: {
    type: "fal-workflow",
    service: INITIAL_GENERATION_SERVICE,
    webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
    outputPath: "payload.images[0]",
    outputMapping: {
      url: "image_url",
    },
    costConfig: {
      type: "fixed",
      value: FLUX_LORA_GENERATION_COST,
    },
  },
};

export const FACE_DETAILER_WORKFLOW_PIPELINE: WorkflowPipeline = {
  1: {
    type: "fal-comfyui-workflow",
    service: FACEDETAILER_SERVICE,
    webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
    outputPath: "payload.outputs",
    costConfig: {
      type: "per_second",
      value: COMFY_PER_SECOND_COST,
    },
  },
};

export const REDO_FACE_WORKFLOW_PIPELINE: WorkflowPipeline = {
  1: {
    type: "fal-comfyui-workflow",
    service: REDO_FACE_SERVICE,
    webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
    outputPath: "payload.outputs",
    costConfig: {
      type: "per_second",
      value: COMFY_PER_SECOND_COST,
    },
    dynamicInputs: [
      {
        type: "training_image",
        field: "training_image_url",
      },
    ],
  },
};

export const FAL_AI_CCSR_WORKFLOW_PIPELINE: WorkflowPipeline = {
  1: {
    type: "fal-workflow",
    service: FAL_AI_CCSR_SERVICE,
    webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
    outputPath: "payload.image",
    costConfig: {
      type: "per_second",
      value: COMFY_PER_SECOND_COST,
    },
  },
};

export const FAL_AI_RECAFT_CLARITY_UPSCALE_WORKFLOW_PIPELINE: WorkflowPipeline =
  {
    1: {
      type: "fal-workflow",
      service: FAL_AI_RECAFT_CLARITY_UPSCALE_SERVICE,
      webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
      outputPath: "payload.image",
      costConfig: {
        type: "per_second",
        value: COMFY_PER_SECOND_COST,
      },
    },
  };

export const FILL_IMAGE_WORKFLOW_PIPELINE: WorkflowPipeline = {
  1: {
    type: "fal-workflow",
    service: FAL_AI_RECAFT_CLARITY_UPSCALE_SERVICE,
    webhook_url: "https://www.picstudio.ai/webhooks/fal/pipeline",
    outputPath: "payload.image",
    costConfig: {
      type: "per_second",
      value: COMFY_PER_SECOND_COST,
    },
  },
};

export const PIPELINES = {
  TRY_ON_WORKFLOW_PIPELINE,
  HEADSHOT_WORKFLOW_PIPELINE,
  HEADSHOT_WORKFLOW_PIPELINE_PASS1_USER_LORA_EXCLUDED,
  FACE_DETAILER_WORKFLOW_PIPELINE,
  SINGLE_PASS_HEADSHOT_WORKFLOW,
  REDO_FACE_WORKFLOW_PIPELINE,
  FAL_AI_CCSR_WORKFLOW_PIPELINE,
};

export const ALLOWED_PIPELINES = [
  "SINGLE_PASS_HEADSHOT_WORKFLOW",
  "HEADSHOT_WORKFLOW_PIPELINE",
  "HEADSHOT_WORKFLOW_PIPELINE_PASS1_USER_LORA_EXCLUDED",
];

export const getNextStep = (
  pipeline: keyof typeof PIPELINES,
  step: number
): WorkflowStep | undefined => {
  const workflow = PIPELINES[pipeline];
  return workflow[step as keyof typeof workflow];
};

export const DEFAULT_INITIAL_GENERATION_WORKFLOW_PIPELINE =
  "HEADSHOT_WORKFLOW_PIPELINE";
export const COMFY_FLUX_UPSCALE_SERVICE = "comfy/Prompt-Hunt/fluxupscalerv2";
export const COMFY_ULTIMATE_SD_UPSCALE_SERVICE =
  "comfy/Prompt-Hunt/upscale-w-ultimate-sd-upscale-w-moondream-production";

export const UPSCALE_SERVICES = {
  COMFY_FLUX_UPSCALE_SERVICE,
  COMFY_ULTIMATE_SD_UPSCALE_SERVICE,
  FAL_AI_RECAFT_CLARITY_UPSCALE_SERVICE,
  FAL_AI_CCSR_SERVICE,
  DEFAULT: COMFY_ULTIMATE_SD_UPSCALE_SERVICE,
};
